// The arena in the low-poly look (gate B4's shots, phase B joined): the ground, walls, pillars and rocks faceted on the
// look's one surface material; the Wanderer and every enemy as figures on the bodies module (src/bodies/figures.ts:
// their motion, the action poses and hit reactions, a boss's phase card, ragdolls that dissolve into motes); the impact
// director (src/feedback/: hits, flashes, particles, telegraphs draped on the ground, weapon trails); "?" and "!" over
// a suspicious or alert head; the navmesh's debug view; a fixed-yaw follow camera, or a framing the handle sets.
// A view: it reads the sim and never writes it.
import * as THREE from "three";
import { createLowPolyLook, type LowPolyLook, type LowPolySceneLook, type LowPolyTier } from "@rpg/look";
import { facet, seeded } from "@rpg/meshkit";
import { createNavDebugView, type NavDebugView } from "@rpg/nav/debug";
import type { Runtime } from "@rpg/runtime";
import type { BodySpec, ShapeSpec } from "@rpg/collision";
import type { EventBus } from "@rpg/kernel";
import type { SpikeContent } from "../content";
import type { GameEvents } from "../core";
import { mountFeedback, reduceMotionOf, type Feedback } from "../feedback";
import { mountFigureSet, type FigureEntry, type FigureSet } from "../bodies/figures";
import { ARENA_CAMERA_YAW, arenaLayout, type ArenaRig } from "./arena";
import { HERO_ID } from "./fight";

/** A framing for shots: look at (x, z) from `yaw` (the camera's back toward it), `dist` m off, `height` up. */
export interface ArenaFrame {
  x: number;
  z: number;
  yaw: number;
  dist: number;
  height: number;
  /** Look-at height above the ground (default 1). */
  lookY?: number;
}

/** The arena's dressed view. */
export interface ArenaView {
  look: LowPolyLook;
  feedback: Feedback;
  figures: FigureSet;
  render(): void;
  /** Frame the camera for a shot (null: follow the hero). */
  frame(f: ArenaFrame | null): void;
  /** Show the navmesh's debug view. */
  navDebug(on: boolean): void;
  state(): { tier: string; figures: number; telegraphs: number; markers: number; navDebug: boolean; frame: ArenaFrame | null };
}

const COLOUR: Record<string, string> = { terrain: "#86b35a", wall: "#9c9284", pillar: "#b3a891", low: "#a89a80", rock: "#8a8274" };

function geometryOf(s: ShapeSpec): THREE.BufferGeometry {
  switch (s.type) {
    case "cuboid":
      return new THREE.BoxGeometry(s.halfExtents.x * 2, s.halfExtents.y * 2, s.halfExtents.z * 2);
    case "cylinder":
      return new THREE.CylinderGeometry(s.radius, s.radius * 1.08, s.halfHeight * 2, 9);
    case "heightfield": {
      const g = new THREE.PlaneGeometry(s.size.x, s.size.z, s.cols, s.rows).rotateX(-Math.PI / 2);
      const p = g.attributes.position!;
      for (let i = 0; i < p.count; i++) p.setY(i, s.heights[i]! * s.size.y);
      return g;
    }
    default:
      return new THREE.BoxGeometry(0.1, 0.1, 0.1);
  }
}

/** The marker glyphs, as geometry: "?" (a hook and a dot) and "!" (a bar and a dot). */
function markerGeometry(kind: "?" | "!"): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  if (kind === "!") {
    parts.push(new THREE.BoxGeometry(0.09, 0.32, 0.09).translate(0, 0.24, 0));
  } else {
    parts.push(new THREE.TorusGeometry(0.11, 0.035, 4, 10, Math.PI * 1.4).rotateZ(-Math.PI * 0.2).translate(0, 0.3, 0));
    parts.push(new THREE.BoxGeometry(0.07, 0.1, 0.07).translate(0, 0.15, 0));
  }
  parts.push(new THREE.BoxGeometry(0.09, 0.09, 0.09).translate(0, 0, 0));
  const pos: number[] = [];
  for (const p of parts) {
    const n = p.index ? p.toNonIndexed() : p;
    pos.push(...(n.getAttribute("position").array as Float32Array));
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  return g;
}

export function mountArenaView(o: {
  runtime: Runtime;
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  content: SpikeContent;
  rig: () => ArenaRig;
  tier: string;
  navDebug?: boolean;
  /** The kernel game's bus (the combat events, for the impact director). */
  events: EventBus<GameEvents>;
  /** A setting's value now (the shake slider, reduce motion). */
  setting?: (id: string) => unknown;
}): ArenaView {
  const reg = o.content.registry;
  const sceneLook = reg.get("looks", "valley-day") as unknown as LowPolySceneLook | undefined;
  if (!sceneLook) throw new Error('arena view: no look "valley-day"');
  const tierId = o.tier === "low" ? "low" : "high";
  const tier = reg.get("tiers", tierId) as unknown as LowPolyTier | undefined;
  if (!tier) throw new Error(`arena view: no tier "${tierId}"`);
  const old: THREE.Object3D[] = [];
  o.scene.traverse((c) => (c as THREE.Light).isLight && old.push(c));
  for (const l of old) l.removeFromParent();
  const look = createLowPolyLook({ renderer: o.renderer, scene: o.scene, camera: o.camera, look: sceneLook, tier, shadowExtent: 20, maxBlobs: 64 });
  const r = o.rig();
  const fight = r.fight;

  // the static world, faceted in its colours on the look's material
  const world = new THREE.Group();
  world.name = "arena-world";
  const rng = seeded(0xa2e7a);
  const layout = arenaLayout();
  for (const b of layout.bodies as BodySpec[]) {
    const shape = b.colliders[0]!.shape;
    const colour = b.id === "terrain" ? COLOUR.terrain : b.id.startsWith("wall") ? COLOUR.wall : b.id.startsWith("pillar") ? COLOUR.pillar : b.id.startsWith("low") ? COLOUR.low : COLOUR.rock;
    const big = shape.type === "heightfield";
    const mesh = new THREE.Mesh(facet(geometryOf(shape), { colour: new THREE.Color(colour!), jitter: big ? 0 : 0.03, tint: big ? 0.05 : 0.08, rng }), look.surface);
    mesh.name = `arena:${b.id}`;
    if (b.position) mesh.position.set(b.position.x, b.position.y, b.position.z);
    if (b.rotation) mesh.quaternion.set(b.rotation.x, b.rotation.y, b.rotation.z, b.rotation.w);
    world.add(mesh);
  }
  o.scene.add(world);
  look.track(world);

  const probe = (x: number, z: number, fromY: number): number | null => r.collision.castRay({ x, y: fromY, z }, { x: 0, y: -1, z: 0 }, 40, { layers: ["terrain", "solid"] })?.point.y ?? null;

  // the impact director (gate B6) on the arena: hits, flashes, particles, telegraphs draped on the ground, trails (no
  // camera rig here: the arena's camera is a fixed-yaw follow, so no shake or kick)
  let figures: FigureSet | null = null;
  const feedback: Feedback = mountFeedback({
    runtime: o.runtime,
    events: o.events,
    scene: o.scene,
    content: o.content,
    rig: null,
    weaponOf: (owner) => figures?.weapon(owner) ?? null,
    heightAt: (x, z) => probe(x, z, 60) ?? r.terrainAt(x, z),
    motion: () => ({ shake: Number(o.setting?.("camera.shake") ?? 1), reduceMotion: reduceMotionOf(o.setting?.("accessibility.reduceMotion")) }),
  });

  // the hero and every enemy as figures on the bodies module (poses, reactions, ragdolls)
  const weaponAttachment = (id: number): string | null => {
    const w = fight.view(id)?.weapon ?? null;
    return w ? (fight.content.combat.attachmentOf.get(w) ?? null) : null;
  };
  const entries = (): FigureEntry[] => {
    const h = r.hero();
    const out: FigureEntry[] = [{ id: HERO_ID, card: fight.heroCard, pos: [h.feet.x, h.feet.y, h.feet.z], yaw: h.yaw, vel: [h.vel.x, h.vel.y, h.vel.z], grounded: h.mode === "ground", attachment: weaponAttachment(HERO_ID) }];
    for (const e of fight.state().enemies) {
      if (!e.alive) continue;
      const kind = fight.content.kinds[e.kind]!;
      const card = e.phase > 1 ? (kind.phases[e.phase - 2]?.card ?? kind.card) : kind.card;
      out.push({ id: e.id, card, pos: e.pos, yaw: e.facing, vel: e.vel, acting: fight.view(e.id)?.move != null });
    }
    return out;
  };
  figures = mountFigureSet({
    runtime: o.runtime,
    scene: o.scene,
    look,
    content: o.content,
    bodies: () => fight.bodies,
    collision: () => r.collision,
    entries,
    probe,
    onAdd: (id, root) => feedback.track(id, root),
    onRemove: (id) => feedback.untrack(id),
    name: "arena view",
  });

  // the "?" and "!" markers: materials made once, now (compiled at the warm-up, never after ready)
  const markerMats = { "?": new THREE.MeshBasicMaterial({ color: "#ffd34d" }), "!": new THREE.MeshBasicMaterial({ color: "#ff4d5e" }) };
  const markerGeos = { "?": markerGeometry("?"), "!": markerGeometry("!") };
  markerMats["?"].name = "arena:marker?";
  markerMats["!"].name = "arena:marker!";
  const warmers: THREE.Object3D[] = [];
  for (const k of ["?", "!"] as const) {
    const m = new THREE.Mesh(markerGeos[k], markerMats[k]);
    m.position.set(0, -50, 0);
    warmers.push(m);
  }
  for (const w of warmers) o.scene.add(w);
  const markers = new Map<number, THREE.Mesh>();
  let seenEvents = 0;
  /** The step each enemy last turned alert or suspicious (its "!" shows for a beat after). */
  const alertedAt = new Map<number, number>();

  const navView: NavDebugView = createNavDebugView(r.nav, { crowd: fight.crowd });
  o.scene.add(navView.group);
  let navOn = o.navDebug ?? false;
  let framing: ArenaFrame | null = null;
  const target = new THREE.Vector3();

  o.runtime.on(
    "late",
    (c) => {
      if (c.mode === "play" && o.runtime.paused) return;
      // the alerts since the last frame (the log is capped and keeps its tail; a reset empties it)
      const ev = fight.events;
      const total = fight.eventsTotal();
      if (total < seenEvents) seenEvents = 0;
      const fresh = Math.min(ev.length, total - seenEvents);
      seenEvents = total;
      for (let i = ev.length - fresh; i < ev.length; i++) {
        const e = ev[i]!;
        if (e.type === "alert") alertedAt.set(e.p.entity, e.step);
      }
      const st = fight.state();
      const live = new Set<number>();
      for (const e of st.enemies) {
        if (!e.alive) continue;
        live.add(e.id);
        let m = markers.get(e.id);
        if (!m) {
          m = new THREE.Mesh(markerGeos["?"], markerMats["?"]);
          m.visible = false;
          o.scene.add(m);
          markers.set(e.id, m);
        }
        // "?" or "!" over the head; the "!" shows for a beat when it turns alert, then goes (a fight full of "!"s reads
        // as noise)
        const want = e.percept.level === "alert" ? "!" : e.percept.level === "suspicious" ? "?" : null;
        const since = st.steps - (alertedAt.get(e.id) ?? -1e9);
        m.visible = want === "?" || (want === "!" && since * r.dt < 1.5);
        const root = figures!.figure(e.id)?.root;
        if (want && root) {
          m.geometry = markerGeos[want];
          m.material = markerMats[want];
          const top = fight.content.kinds[e.kind]!.body.height * (e.phase > 1 ? 1.08 : 1) + 0.35;
          m.position.set(root.position.x, root.position.y + top, root.position.z);
          m.rotation.y += c.dt * 2;
        }
      }
      for (const [id, m] of markers) {
        if (live.has(id)) continue;
        m.removeFromParent();
        markers.delete(id);
      }
      // the camera
      const hero = figures!.figure(HERO_ID)?.root.position ?? new THREE.Vector3(r.hero().feet.x, r.hero().feet.y, r.hero().feet.z);
      const f = framing;
      if (f) {
        const ty = r.terrainAt(f.x, f.z) + (f.lookY ?? 1);
        target.set(f.x, ty, f.z);
        o.camera.position.set(f.x - Math.sin(f.yaw) * f.dist, ty + f.height, f.z - Math.cos(f.yaw) * f.dist);
      } else {
        target.set(hero.x, hero.y + 1, hero.z);
        o.camera.position.set(hero.x - Math.sin(ARENA_CAMERA_YAW) * 11, target.y + 7.5, hero.z - Math.cos(ARENA_CAMERA_YAW) * 11);
      }
      o.camera.lookAt(target);
      look.focus(target);
      navView.group.visible = navOn;
      if (navOn) navView.update();
    },
    // after the figures (-15)
    { order: -14, name: "arena view: markers and camera" },
  );
  addEventListener("resize", () => look.resize(innerWidth, innerHeight));
  // compile every program the arena will use (the debug view, the markers and the feedback's included), then hide the
  // extras. The warm-up draws a frame, and only a drawn object's program is sure to match play's (three's `compile`
  // keys a program by the render target current at the time): the stand-ins sit in front of the boot camera for it.
  navView.group.visible = true;
  const ahead = new THREE.Vector3();
  o.camera.getWorldDirection(ahead);
  for (const [i, w] of warmers.entries()) w.position.copy(o.camera.position).addScaledVector(ahead, 4).add(new THREE.Vector3((i - 0.5) * 0.3, 0, 0));
  const front = o.camera.position.clone().addScaledVector(ahead, 6);
  const clearFeedback = feedback.warm([front.x, r.terrainAt(front.x, front.z), front.z]);
  look.warm();
  clearFeedback();
  navView.group.visible = navOn;
  for (const w of warmers) w.removeFromParent();

  return {
    look,
    feedback,
    figures,
    render: () => look.render(),
    frame(f) {
      framing = f;
    },
    navDebug(on) {
      navOn = on;
    },
    state: () => ({ tier: tierId, figures: figures!.report().standing, telegraphs: feedback.telegraphs.state().length, markers: [...markers.values()].filter((m) => m.visible).length, navDebug: navOn, frame: framing }),
  };
}
